import React, { useState } from "react";
import { api } from "../lib/api";
import { Stethoscope, Loader2, CheckCircle2, AlertTriangle, XCircle, Wand2 } from "lucide-react";

/**
 * Smart editor diagnostics panel. Runs POST /editor/suggest-fixes on the current
 * draft, lists bilingual issues by severity, and one-click applies the safe
 * auto-fixes (onApplyFixed swaps in the corrected model).
 */
export const PlanDoctor = ({ model, onApplyFixed, lang = "ar" }) => {
  const isAr = (lang || "ar").toLowerCase().startsWith("ar");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try { setRes(await api.editorSuggestFixes(model)); }
    catch { setRes({ error: true }); }
    finally { setLoading(false); }
  };
  const apply = () => { if (res?.fixed_model) { onApplyFixed(res.fixed_model); setRes(null); } };

  const sevIcon = (sev) => sev === "error"
    ? <XCircle className="h-4 w-4 shrink-0 text-red-400" />
    : <AlertTriangle className="h-4 w-4 shrink-0 text-amber-400" />;

  return (
    <div className="rounded-2xl border border-border bg-card/60 p-4" data-testid="plan-doctor">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Stethoscope className="h-5 w-5 text-primary" />
          <h3 className="font-bold">{isAr ? "الفحص الذكي" : "Smart check"}</h3>
        </div>
        <button onClick={run} disabled={loading} data-testid="plan-doctor-run"
          className="rounded-full border border-primary/40 px-3 py-1.5 text-xs font-bold text-primary transition-colors hover:bg-primary/10 disabled:opacity-50">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (isAr ? "افحص المخطط" : "Scan plan")}
        </button>
      </div>

      {res?.error && <p className="text-sm text-destructive">{isAr ? "تعذّر الفحص" : "Scan failed"}</p>}

      {res && !res.error && res.ok && (
        <div className="flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm font-bold text-emerald-400" data-testid="plan-doctor-clean">
          <CheckCircle2 className="h-4 w-4" /> {isAr ? "لا مشاكل - المخطط سليم" : "No issues - the plan is sound"}
        </div>
      )}

      {res && !res.error && !res.ok && (
        <div className="space-y-2">
          {res.issues.map((it, i) => (
            <div key={i} className="flex items-start gap-2 rounded-xl border border-border/60 bg-background/40 p-3" data-testid="plan-doctor-issue">
              {sevIcon(it.severity)}
              <div className="min-w-0">
                <p className="text-sm font-bold">{isAr ? it.title_ar : it.title_en}</p>
                <p className="text-xs text-muted-foreground">{isAr ? it.detail_ar : it.detail_en}{it.auto_fixable ? (isAr ? " · قابل للإصلاح تلقائيًا" : " · auto-fixable") : ""}</p>
              </div>
            </div>
          ))}
          {res.fixable_count > 0 && (
            <button onClick={apply} data-testid="plan-doctor-apply"
              className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-2.5 text-sm font-bold text-primary-foreground transition-opacity hover:opacity-90">
              <Wand2 className="h-4 w-4" /> {isAr ? `إصلاح تلقائي (${res.fixable_count})` : `Auto-fix (${res.fixable_count})`}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default PlanDoctor;
